import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import LogLayout from "../components/LogLayout";
import { Clock, Search, Filter, ChevronLeft, ChevronRight, Loader2, FileText, User, Activity } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const ACTION_LABELS = {
  invoice_created: "Facture creee",
  invoice_auto_created: "Facture creee (auto)",
  invoice_updated: "Facture modifiee",
  invoice_deleted: "Facture supprimee",
  invoice_status_changed: "Statut facture change",
  ocr_manually_corrected: "Correction OCR manuelle",
  photo_uploaded: "Document uploade",
  photo_deleted: "Document supprimé",
  course_created: "Course creee",
  course_assigned: "Course assignee",
  course_status_changed: "Statut course change",
  ceiling_set: "Plafond défini",
  monthly_sales_set: "CA mensuel enregistré",
};

const ACTION_COLORS = {
  invoice_created: "bg-blue-500/100/15 text-blue-400",
  invoice_auto_created: "bg-cyan-500/100/15 text-cyan-400",
  invoice_updated: "bg-amber-500/100/15 text-amber-400",
  invoice_deleted: "bg-red-500/100/15 text-red-400",
  invoice_status_changed: "bg-emerald-500/15 text-emerald-400",
  ocr_manually_corrected: "bg-violet-500/15 text-violet-400",
  photo_uploaded: "bg-sky-500/15 text-sky-400",
  course_created: "bg-indigo-500/15 text-indigo-400",
  course_status_changed: "bg-yellow-500/15 text-yellow-400",
};

export default function LogActivityPage() {
  const [logs, setLogs] = useState({ logs: [], total: 0, page: 1, pages: 1 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filterAction, setFilterAction] = useState("");
  const [filterEntity, setFilterEntity] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (filterAction) params.action = filterAction;
      if (filterEntity) params.entity_type = filterEntity;
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/log/activity`, { headers: getHeaders(), params }),
        axios.get(`${API}/log/activity/stats`, { headers: getHeaders() }),
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch { }
    finally { setLoading(false); }
  }, [page, filterAction, filterEntity]);

  useEffect(() => { load(); }, [load]);

  const formatDate = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const formatDetails = (details) => {
    if (!details || typeof details !== "object") return "";
    const parts = [];
    if (details.new) parts.push(`Statut: ${details.new}`);
    if (details.old) parts.push(`(ancien: ${details.old})`);
    if (details.amount) parts.push(`Montant: ${details.amount?.toLocaleString()} FCFA`);
    if (details.insurer) parts.push(`Assureur: ${details.insurer}`);
    if (details.filename) parts.push(`Fichier: ${details.filename}`);
    if (details.source) parts.push(`Source: ${details.source}`);
    return parts.join(" | ");
  };

  return (
    <LogLayout title="Journal d'activité" subtitle="Historique de toutes les actions exécutées sur la plateforme">
      <div data-testid="log-activity-page" className="space-y-4">
        {/* KPI bar */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-blue-500/100/10 border border-blue-500/20 rounded-xl p-4">
              <p className="text-2xl font-bold text-white">{stats.total}</p>
              <p className="text-[10px] text-slate-500 uppercase">Actions totales</p>
            </div>
            {Object.entries(stats.by_action || {}).slice(0, 3).map(([action, count]) => (
              <div key={action} className="bg-slate-900 border border-white/5 rounded-xl p-4">
                <p className="text-lg font-bold text-white">{count}</p>
                <p className="text-[10px] text-slate-500 uppercase truncate">{ACTION_LABELS[action] || action}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-slate-900 border border-white/10 rounded-xl p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }}
              placeholder="Filtrer par action..." className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-white/10 rounded-lg text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none text-sm" data-testid="activity-search" />
          </div>
          <select value={filterEntity} onChange={e => { setFilterEntity(e.target.value); setPage(1); }} className="bg-slate-950 border border-white/10 rounded-lg text-slate-300 px-3 py-2 text-sm" data-testid="activity-filter-entity">
            <option value="">Toutes entites</option>
            <option value="invoice">Factures</option>
            <option value="course">Courses</option>
          </select>
        </div>

        {/* Log list */}
        <div className="bg-slate-900 border border-white/10 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-400" />
              <p className="text-sm text-slate-300">{logs.total} action(s)</p>
            </div>
            <p className="text-xs text-slate-500">Page {logs.page}/{logs.pages}</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-400" /></div>
          ) : logs.logs.length === 0 ? (
            <p className="text-slate-500 text-center py-10">Aucune activite</p>
          ) : (
            <div className="divide-y divide-white/5">
              {logs.logs.map(log => (
                <div key={log.id} className="flex items-start gap-3 px-5 py-3 hover:bg-white/[0.02]" data-testid={`activity-${log.id}`}>
                  <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                    {log.entity_type === "invoice" ? <FileText className="w-3.5 h-3.5 text-blue-400" /> :
                      <Clock className="w-3.5 h-3.5 text-slate-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${ACTION_COLORS[log.action] || "bg-white/50/15 text-slate-400"}`}>
                        {ACTION_LABELS[log.action] || log.action}
                      </span>
                      <span className="text-xs text-slate-500">{log.entity_type} {log.entity_id?.slice(0, 8)}...</span>
                    </div>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <p className="text-xs text-slate-500 mt-0.5">{formatDetails(log.details)}</p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-slate-400">{formatDate(log.timestamp)}</p>
                    <p className="text-[10px] text-slate-600 flex items-center justify-end gap-1 mt-0.5">
                      <User className="w-2.5 h-2.5" /> {log.user_email}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {logs.pages > 1 && (
            <div className="flex items-center justify-center gap-2 py-3 border-t border-white/5">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-slate-400"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-xs text-slate-400">{page} / {logs.pages}</span>
              <Button variant="ghost" size="sm" disabled={page >= logs.pages} onClick={() => setPage(p => p + 1)} className="text-slate-400"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
        </div>
      </div>
    </LogLayout>
  );
}
